import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type {
  UserComputerScienceBasics,
  UserComputerScienceBasicsGetStarted,
  UserField,
  UserGoal,
  UserHowHeard,
} from 'src/common/enums';
import type { OccupationDetailsFormData } from 'src/common/interfaces/occupation-details';

export const PRE_SIGNUP_STATE_STORAGE_KEY = 'thm_pre_signup_state';
const PRE_SIGNUP_STATE_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StoredPreSignupState {
  timestamp: number;
  state: PreSignupOnboardingState;
}

export interface PreSignupOnboardingState {
  demographics: {
    goal?: UserGoal;
    computerScienceBasics?: UserComputerScienceBasics;
    computerScienceBasicsGetStarted?: UserComputerScienceBasicsGetStarted;
    field?: UserField;
    howHeard?: UserHowHeard;
  };

  occupationDetails: OccupationDetailsFormData;
  selectedPath?: string; // Store the path code selected on welcome page
  redirectUrl?: string; // Store the complete redirect URL for post-signup navigation
}

const initialState: PreSignupOnboardingState = {
  demographics: {},
  occupationDetails: {},
};

export const preSignupOnboardingSlice = createSlice({
  name: 'preSignupOnboarding',
  initialState,
  reducers: {
    updateDemographics: (state, action: PayloadAction<Partial<PreSignupOnboardingState['demographics']>>) => {
      state.demographics = { ...state.demographics, ...action.payload };
    },

    updateOccupationDetails: (state, action: PayloadAction<Partial<PreSignupOnboardingState['occupationDetails']>>) => {
      state.occupationDetails = { ...state.occupationDetails, ...action.payload };
    },
  },
});

export const { updateDemographics, updateOccupationDetails } = preSignupOnboardingSlice.actions;
// TODO: Move the reducer to the app store at root level if experiment is successful
export const preSignupOnboardingReducer = preSignupOnboardingSlice.reducer;

// Helper function to save pre-signup state to localStorage
export const savePreSignupStateToStorage = (state: PreSignupOnboardingState) => {
  try {
    const dataToStore: StoredPreSignupState = {
      timestamp: Date.now(),
      state,
    };
    localStorage.setItem(PRE_SIGNUP_STATE_STORAGE_KEY, JSON.stringify(dataToStore));
  } catch (error) {
    console.error('Failed to save pre-signup state to storage', error);
  }
};

export const loadPreSignupStateFromStorage = (): PreSignupOnboardingState | null => {
  try {
    const storedItem = localStorage.getItem(PRE_SIGNUP_STATE_STORAGE_KEY);
    if (!storedItem) return null;

    const parsedItem = JSON.parse(storedItem) as StoredPreSignupState;
    const now = Date.now();

    if (now - parsedItem.timestamp > PRE_SIGNUP_STATE_EXPIRATION_MS) {
      localStorage.removeItem(PRE_SIGNUP_STATE_STORAGE_KEY);
      return null;
    }

    return parsedItem.state || null;
  } catch (error) {
    console.error('Failed to load pre-signup state from storage', error);
    // If parsing fails, clear the invalid item
    localStorage.removeItem(PRE_SIGNUP_STATE_STORAGE_KEY);
    return null;
  }
};
